import React, { useState, useEffect } from 'react';
import { Box, MenuItem, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import GolfSpotSettingWidget from '../GolfSpotSettingWidget';
import GolfAreaEditModal from '../StoreModals/GolfAreaEditModal';
import GolfAreaSettingModal from '../StoreModals/GolfAreaSettingModal';
import GolfAreaDeleteModal from '../StoreModals/GolfAreaDeleteModal';

const SpotPlacementSettings = ({
  spotAreas,
  selectedAreaId,
  setSelectedAreaId,
  control,
  setValue,
  handleCheckSpotSetting,
  handleSpotCreateModal,
  handleSpotCreateModalClose,
  openSpotCreateModal,
  openSpotSettingModal,
  handleSpotSettingModal,
  handleSpotSettingModalClose,
  handleSpotEditModalOpen,
  selectedAreaForEdit,
  branchId,
  refreshSpotAreas,
  refreshSpotAreasForModal,
}) => {
  const { t } = useTranslation();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedAreaForDelete, setSelectedAreaForDelete] = useState(null);
  const [deleteSuccessCallback, setDeleteSuccessCallback] = useState(null);
  const [localSpotAreas, setLocalSpotAreas] = useState(spotAreas);

  const handleDeleteModalOpen = (area, onDeleteSuccessCallback) => {
    setSelectedAreaForDelete(area);
    setOpenDeleteModal(true);
    if (onDeleteSuccessCallback) {
      setDeleteSuccessCallback(() => onDeleteSuccessCallback);
    }
  };

  const handleDeleteModalClose = () => {
    setOpenDeleteModal(false);
    setSelectedAreaForDelete(null);
    setDeleteSuccessCallback(null);
  };

  const handleDeleteSuccess = (deletedArea, updatedAreas = null) => {
    if (deleteSuccessCallback) {
      deleteSuccessCallback(deletedArea);
      setDeleteSuccessCallback(null);
    }

    const currentAreas = updatedAreas !== null ? updatedAreas : spotAreas;

    setLocalSpotAreas(currentAreas);

    if (currentAreas.length === 0) {
      setSelectedAreaId(null);
      setValue('selectedArea', '');
      return;
    }

    if (selectedAreaId === deletedArea.golfAreaId) {
      const remainingAreas = currentAreas.filter(area => area.golfAreaId !== deletedArea.golfAreaId);
      if (remainingAreas.length > 0) {
        setSelectedAreaId(remainingAreas[0].golfAreaId);
        setValue('selectedArea', remainingAreas[0].golfAreaId.toString());
      } else {
        setSelectedAreaId(null);
        setValue('selectedArea', '');
      }
    }
  };

  useEffect(() => {
    setLocalSpotAreas(spotAreas);
  }, [spotAreas]);

  useEffect(() => {
    if (selectedAreaId && spotAreas.length > 0) {
      const areaExists = spotAreas.some(area => area.golfAreaId === selectedAreaId);
      if (areaExists) {
        setValue('selectedArea', selectedAreaId.toString());
      }
    }
  }, [selectedAreaId, spotAreas, setValue]);

  useEffect(() => {
    if (localSpotAreas.length > 0 && selectedAreaId) {
      const areaExists = localSpotAreas.some(area => area.golfAreaId === selectedAreaId);
      if (areaExists) {
        setValue('selectedArea', selectedAreaId.toString());
      }
    }
  }, [localSpotAreas, selectedAreaId, setValue]);

  useEffect(() => {
    if (localSpotAreas.length === 0) {
      setSelectedAreaId(null);
      setValue('selectedArea', '');
    }
  }, [localSpotAreas, setValue, setSelectedAreaId]);

  return (
    <>
      <Box>
        {localSpotAreas.length === 0 ? (
          <Box
            sx={{
              width: '100%',
              height: '112px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
            }}>
            <FormInputDropdown
              color="info"
              control={control}
              sx={{ width: '280px' }}
              rules={{ required: true }}
              size="small"
              required
              fullWidth
              variant="outlined"
              disabledItem={<MenuItem disabled>{t('Area Select')}</MenuItem>}
            />
            <Typography
              sx={{
                width: '240px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #0F7F85',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: '600',
                color: '#0F7F85',
                cursor: 'pointer',
                gap: '8px',
              }}>
              <img src="/images/icons/area.svg" alt="area" />
              {t('Spot zoning setting')}
            </Typography>
            <Typography
              sx={{
                width: '240px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: '600',
                background: '#0F7F85',
                color: '#FFFFFF',
                cursor: 'pointer',
                gap: '8px',
              }}
              onClick={handleSpotCreateModal}>
              <img src="/images/icons/add.svg" alt="add-zone" />
              {t('Spot create')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '112px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
            }}>
            <FormInputDropdown
              color="info"
              control={control}
              name="selectedArea"
              sx={{ width: '280px' }}
              rules={{ required: true }}
              size="small"
              required
              fullWidth
              variant="outlined"
              options={localSpotAreas.map(area => ({
                value: area.golfAreaId.toString(),
                label: area.golfAreaInfo.areaName,
              }))}
              disabledItem={<MenuItem disabled> {t('Area Select')}</MenuItem>}
              onChange={e => {
                const areaId = parseInt(e.target.value);
                setSelectedAreaId(areaId);
                setValue('selectedArea', e.target.value);
              }}
            />
            <Typography
              sx={{
                width: '240px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #0F7F85',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: '600',
                color: '#0F7F85',
                cursor: 'pointer',
                gap: '8px',
              }}
              onClick={handleSpotSettingModal}>
              <img src="/images/icons/area.svg" alt="area" />
              {t('Zoning')}
            </Typography>
            <Typography
              sx={{
                width: '240px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                fontSize: '20px',
                fontWeight: '600',
                background: '#0F7F85',
                color: '#FFFFFF',
                cursor: 'pointer',
                gap: '8px',
              }}
              onClick={handleSpotCreateModal}>
              <img src="/images/icons/add.svg" alt="add-zone" />
              {t('Create Zone')}
            </Typography>
          </Box>
        )}
      </Box>
      <>
        {localSpotAreas.length === 0 ? (
          <Box
            sx={{
              width: '100%',
              pb: '36px',
              height: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography sx={{ fontSize: '24px', fontWeight: 'semibold', color: '#000' }}>
              {t('No created spot area')}
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 'medium', color: '#737373' }}>
              {t('Create spot area')}
            </Typography>
            <img src="/images/icons/spot-not-found.svg" alt="spot-not-found" />
            <Button
              sx={{
                width: '178px',
                height: '48px',
                background: '#0F7F85',
                color: '#FFFFFF',
                fontSize: '15px',
                '&:hover': { background: '#ffffff', color: '#0F7F85', border: '1px solid #0F7F85' },
              }}
              onClick={handleSpotCreateModal}>
              {t('Spot create')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ width: '100% ', pb: '36px' }}>
            {selectedAreaId &&
              (() => {
                const selectedArea = localSpotAreas.find(area => area.golfAreaId === selectedAreaId);
                if (!selectedArea) return null;
                // 사용중, 사용대기 , 사용불가 카운트 계산 하는 곳
                const inUseCount = selectedArea.boxes
                  ? selectedArea.boxes.filter(box => box.spotStatus === 'READY').length
                  : 0;
                const waitingCount = selectedArea.boxes
                  ? selectedArea.boxes.filter(box => box.spotStatus === 'SUSPENDED').length
                  : 0;
                const unusableCount = selectedArea.boxes
                  ? selectedArea.boxes.filter(box => box.spotStatus === 'DISABLED').length
                  : 0;

                return (
                  <Box key={selectedArea.golfAreaId} sx={{ mb: '30px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        mb: '32px',
                      }}>
                      <Box sx={{ height: '64px' }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: '600', mb: '8px', color: '#0F7F85' }}>
                          {selectedArea.golfAreaInfo.areaName}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: '400', mb: '36px', color: '#000' }}>
                          {selectedArea.golfAreaInfo.areaMemo}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: '64px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'row',
                          padding: '10.5px',
                        }}>
                        <Box sx={{ borderRight: '2px solid #D9D9D9' }}>
                          <Box
                            sx={{
                              width: '132px',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}>
                            <Box
                              sx={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#0F7F85',
                              }}></Box>
                            <Typography sx={{ fontSize: '16px', color: '#000000' }}>{t('In use')}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '20px', color: '#000000', fontWeight: 'bold' }}>
                              {inUseCount}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ borderRight: '2px solid #D9D9D9' }}>
                          <Box
                            sx={{
                              width: '132px',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}>
                            <Box
                              sx={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #D9D9D9',
                              }}></Box>
                            <Typography sx={{ fontSize: '16px', color: '#000000' }}>{t('Waiting for use')}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '20px', color: '#000000', fontWeight: 'bold' }}>
                              {waitingCount}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Box
                            sx={{
                              width: '132px',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}>
                            <Box
                              sx={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#A6A6A6',
                              }}></Box>
                            <Typography sx={{ fontSize: '16px', color: '#000000' }}>{t('Unusable')}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '20px', color: '#000000', fontWeight: 'bold' }}>
                              {unusableCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${selectedArea.golfAreaInfo.columnCount}, auto)`,
                        gap: '32px',
                        justifyContent: 'center',
                      }}>
                      {selectedArea.boxes &&
                        selectedArea.boxes.map(box => (
                          <GolfSpotSettingWidget
                            key={box.id}
                            box={box}
                            handleCheckSpotSetting={newStatus => handleCheckSpotSetting(box.id, newStatus)}
                            refreshSpotAreas={refreshSpotAreasForModal}
                          />
                        ))}
                    </Box>
                  </Box>
                );
              })()}
          </Box>
        )}
      </>
      <GolfAreaEditModal
        open={openSpotCreateModal}
        handleClose={handleSpotCreateModalClose}
        selectedArea={selectedAreaForEdit}
        branchId={branchId}
        refreshSpotAreas={refreshSpotAreas}
        setSelectedAreaId={setSelectedAreaId}
        setValue={setValue}
      />
      <GolfAreaSettingModal
        open={openSpotSettingModal}
        handleClose={handleSpotSettingModalClose}
        spotAreas={spotAreas}
        refreshSpotAreas={refreshSpotAreasForModal}
        handleCreateModalOpen={handleSpotCreateModal}
        handleEditModalOpen={handleSpotEditModalOpen}
        handleDeleteModalOpen={handleDeleteModalOpen}
      />
      <GolfAreaDeleteModal
        open={openDeleteModal}
        handleClose={handleDeleteModalClose}
        area={selectedAreaForDelete}
        refreshSpotAreas={refreshSpotAreasForModal}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </>
  );
};

export default SpotPlacementSettings;
